import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../Common/_hooks/useAppSelector';
import useProductCatalogCategories from './useProductCatalogCategories';
import useProductCatalogFiltering from './useProductCatalogFiltering';

export interface ProductSearchOption {
  key: string
  type: 'category' | 'filter'
  label: string
  group: string
  detail?: string
  searchText: string
  path?: string
  typeId?: number
  valueId?: number
}

export default function useProductCatalogSearchOptions(includeCategoryOptions = true) {
  const [t] = useTranslation('products');
  const [tR] = useTranslation('routes');
  const viewProductAttributeTypes = useAppSelector(state => state.get('appData').get('viewProductAttributeTypes'));
  const viewProductAttributeValues = useAppSelector(state => state.get('appData').get('viewProductAttributeValues'));
  const {
    childrenByParentId,
    getCategoryUrlSlug,
    topLevelCategories,
  } = useProductCatalogCategories();
  const {
    activeFiltersByType,
  } = useProductCatalogFiltering();

  const baseProductSearchOptions = useMemo(() => {
    const options: ProductSearchOption[] = [];
    const productsPath = tR('/products');

    if (includeCategoryOptions) {
      topLevelCategories.forEach(category => {
        const categoryUrlSlug = getCategoryUrlSlug(category);

        options.push({
          key: 'category:' + category.get('id'),
          type: 'category',
          label: category.get('name'),
          group: t('Categories'),
          searchText: [
            category.get('name'),
            category.get('slug'),
            category.get('url_slug'),
            categoryUrlSlug,
          ].join(' '),
          path: productsPath + '/' + categoryUrlSlug,
        });

        (childrenByParentId.get(category.get('id')) ?? []).forEach(childCategory => {
          const childCategoryUrlSlug = getCategoryUrlSlug(childCategory);

          options.push({
            key: 'category:' + childCategory.get('id'),
            type: 'category',
            label: childCategory.get('name'),
            group: t('Categories'),
            detail: category.get('name'),
            searchText: [
              childCategory.get('name'),
              childCategory.get('slug'),
              childCategory.get('url_slug'),
              childCategoryUrlSlug,
              category.get('name'),
              category.get('url_slug'),
              categoryUrlSlug,
            ].join(' '),
            path: productsPath + '/' + categoryUrlSlug + '/' + childCategoryUrlSlug,
          });
        });
      });
    }

    viewProductAttributeValues.forEach(value => {
      const type = viewProductAttributeTypes.get(String(value.get('type')));
      if (!type) return;

      options.push({
        key: 'filter:' + value.get('type') + ':' + value.get('id'),
        type: 'filter',
        label: value.get('name'),
        group: t('Filters'),
        detail: type.get('name'),
        searchText: [
          value.get('name'),
          value.get('slug'),
          type.get('name'),
          type.get('slug'),
        ].join(' '),
        typeId: value.get('type'),
        valueId: value.get('id'),
      });
    });

    return options;
  }, [childrenByParentId, getCategoryUrlSlug, includeCategoryOptions, t, tR, topLevelCategories, viewProductAttributeTypes, viewProductAttributeValues])

  return useMemo(() => {
    return baseProductSearchOptions.filter(option => {
      if (option.type !== 'filter' || option.typeId === undefined || option.valueId === undefined) {
        return true;
      }

      return activeFiltersByType.get(option.typeId)?.has(String(option.valueId)) !== true;
    });
  }, [activeFiltersByType, baseProductSearchOptions])
}
